import {Color, Icon} from '@benchling/nucleus-ui';
import {Alert, Box, createStyles, Loader, SimpleGrid, Stack, Text} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import React, {useEffect, useRef, useState} from 'react';

import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import AutoCreateBaseCompoundChip from 'chemistry/molecules/AutoCreateBaseCompoundChip';
import {MoleculeFieldLabel, MoleculeFieldValue} from 'chemistry/molecules/tabs/MoleculeStructureField';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';

import * as MoleculeDAO from './MoleculeDAO';
import type {ParseCounterpartsResponse} from './MoleculeDAO';

const DEBOUNCE_MS = 500;
const UNRECOGNIZED_FRAGMENTS_ERROR_CODE = 'MULTIPLE_UNRECOGNIZED_FRAGMENTS';
const DEFAULT_PLACEHOLDER = 'Start by sketching a molecule';
const ERROR_PLACEHOLDER = 'Unable to identify molecule compound';

const useStyles = createStyles(() => ({
  fieldGroup: {
    fontSize: 13,
  },
  fieldValue: {
    border: `1px solid ${Color.GRAY_200}`,
    cursor: 'not-allowed',
  },
  loader: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
}));

function getErrorAlertTitle(errorCode: string | null): string | undefined {
  switch (errorCode) {
    case UNRECOGNIZED_FRAGMENTS_ERROR_CODE:
      return 'Your structure contains one or more unrecognized fragments.';
    default:
      return undefined;
  }
}

function getErrorAlertMessage(errorCode: string | null, fallbackMessage: string): string {
  switch (errorCode) {
    case UNRECOGNIZED_FRAGMENTS_ERROR_CODE:
      return 'If you intended to include a salt or solvent with your molecule, contact your admin to add it to your organization\u2019s library before registering this molecule. Otherwise, simplify your structure to a single base molecule.';
    default:
      return fallbackMessage;
  }
}

function renderCompoundContent(
  result: ParseCounterpartsResponse | null,
  entityName: string | undefined,
  errorCode: string | null
): JSX.Element {
  if (result?.existingParents?.[0]) {
    return <BioentityChip id={result.existingParents[0].id} size={ChipSize.SMALL} />;
  }
  if (result?.baseStructure?.canonicalCxSmiles && result?.counterparts?.length) {
    return <AutoCreateBaseCompoundChip entityName={entityName} />;
  }
  if (result?.baseStructure?.canonicalCxSmiles) {
    return <Text span={true}>{result.baseStructure.canonicalCxSmiles}</Text>;
  }
  return (
    <Text span={true}>
      {errorCode === UNRECOGNIZED_FRAGMENTS_ERROR_CODE ? ERROR_PLACEHOLDER : DEFAULT_PLACEHOLDER}
    </Text>
  );
}

interface ChemicalCounterpartFieldsProps {
  sketcherValue: string | null;
  schemaId?: string;
  entityName?: string;
  setHasBlockingError: (blocked: boolean) => void;
  setIsParsing: (parsing: boolean) => void;
  onHasCounterpartsChange?: (hasCounterparts: boolean) => void;
}

export default function ChemicalCounterpartFields({
  sketcherValue,
  schemaId,
  entityName,
  setHasBlockingError,
  setIsParsing,
  onHasCounterpartsChange,
}: ChemicalCounterpartFieldsProps): JSX.Element {
  const {classes} = useStyles();
  const [debouncedValue] = useDebouncedValue(sketcherValue, DEBOUNCE_MS);
  const [result, setResult] = useState<ParseCounterpartsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  // Immediately invalidate any in-flight request and block submission when the sketcher
  // changes. This closes the debounce window where a stale response could be applied or
  // the user could submit before parsing completes.
  useEffect(() => {
    ++requestIdRef.current;
    if (sketcherValue != null && sketcherValue !== EMPTY_MOLFILE) {
      setIsParsing(true);
      onHasCounterpartsChange?.(false);
    }
  }, [sketcherValue, setIsParsing, onHasCounterpartsChange]);

  // Fetch parsed counterparts once the debounced value settles. Responses are discarded
  // if requestIdRef has advanced since the call was initiated (i.e. the user changed the
  // sketcher again before the response arrived).
  useEffect(() => {
    if (debouncedValue == null || debouncedValue === EMPTY_MOLFILE) {
      ++requestIdRef.current;
      setResult(null);
      setError(null);
      setErrorCode(null);
      setLoading(false);
      setIsParsing(false);
      setHasBlockingError(false);
      onHasCounterpartsChange?.(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setIsParsing(true);
    setError(null);
    setErrorCode(null);

    MoleculeDAO.parseCounterparts(ChemicalFormatType.MOLFILE, debouncedValue, schemaId).then(
      (response) => {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        setResult(response);
        setLoading(false);
        setIsParsing(false);
        setHasBlockingError(false);
        onHasCounterpartsChange?.((response.counterparts ?? []).length > 0);
      },
      (err) => {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }
        const errorMessage = err?.responseJSON?.error ?? 'Failed to parse counterparts.';
        const code = err?.responseJSON?.errorCode ?? null;
        setError(errorMessage);
        setErrorCode(code);
        setResult(null);
        setLoading(false);
        setIsParsing(false);
        setHasBlockingError(code === UNRECOGNIZED_FRAGMENTS_ERROR_CODE);
        onHasCounterpartsChange?.(false);
      }
    );
  }, [debouncedValue, schemaId, setIsParsing, setHasBlockingError, onHasCounterpartsChange]);

  return (
    <Stack spacing="md" mb="md">
      <Box className={classes.fieldGroup}>
        <MoleculeFieldLabel>
          Molecule Compound
          <FieldRequiredIndicator /> {loading && <Loader size="xs" className={classes.loader} />}
        </MoleculeFieldLabel>
        <MoleculeFieldValue className={classes.fieldValue}>
          {renderCompoundContent(result, entityName, errorCode)}
        </MoleculeFieldValue>
      </Box>

      <Box className={classes.fieldGroup}>
        <SimpleGrid cols={2} spacing={12}>
          <MoleculeFieldLabel>Salt or Solvent</MoleculeFieldLabel>
          <MoleculeFieldLabel>Stoichiometric Ratio</MoleculeFieldLabel>
        </SimpleGrid>
        {result?.counterparts?.length ? (
          result.counterparts.map((counterpart, index) => (
            <SimpleGrid key={counterpart.id} cols={2} spacing={12} mt={index > 0 ? 8 : 0}>
              <MoleculeFieldValue className={classes.fieldValue}>
                <Text span={true}>{counterpart.name}</Text>
              </MoleculeFieldValue>
              <MoleculeFieldValue className={classes.fieldValue}>
                <Text span={true}>
                  {counterpart.ratioNumerator}:{counterpart.ratioDenominator}
                </Text>
              </MoleculeFieldValue>
            </SimpleGrid>
          ))
        ) : (
          <SimpleGrid cols={2} spacing={12}>
            <MoleculeFieldValue className={classes.fieldValue}>
              <Text span={true}>Salts or solvents that you add to the sketch will populate here</Text>
            </MoleculeFieldValue>
            <MoleculeFieldValue className={classes.fieldValue}>
              <Text span={true}>--</Text>
            </MoleculeFieldValue>
          </SimpleGrid>
        )}
      </Box>

      {error && (
        <Alert
          variant="error"
          icon={<Icon icon="exclamation-circle" />}
          title={getErrorAlertTitle(errorCode)}
        >
          {getErrorAlertMessage(errorCode, error)}
        </Alert>
      )}
    </Stack>
  );
}
